import { useComposerEditorStore } from "#features/conversations/stores/useComposerEditorStore.ts";
import { useSubscriptionTier } from "#hooks/useSubscriptionTier.ts";
import { useCallback, useMemo, useRef, useState } from "react";

export const useFilesDragAndDrop = (handleFileSelected: (selectedFiles: File[]) => void, isMutating: boolean) => {
    const [isDragging, setIsDragging] = useState(false);
    const { isFreemium } = useSubscriptionTier();
    // A file dropped while the composer's editor is unavailable couldn't be sent, so the page doesn't take it.
    const isEditorUnavailable = useComposerEditorStore(state => state.isEditorUnavailable);
    // dragCounter is used to track the number of nested dragenter events.
    // This ensures that the dragging state remains active until the user completely leaves the drop zone.
    const dragCounter = useRef(0);
    // The drop zone is active if a drag is in progress and no mutation is occurring.
    const isDropZoneActive = useMemo(() => isDragging && !isMutating, [isDragging, isMutating]);

    const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (isMutating || isFreemium || isEditorUnavailable) {
            return;
        }

        const types = Array.from(event.dataTransfer.types);

        // Prevent drag if the user is just selecting text (i.e., not dragging a file)
        if (!types.includes("Files")) {
            return;
        }

        event.preventDefault();
        dragCounter.current++;
        setIsDragging(true);
    }, [isEditorUnavailable, isFreemium, isMutating]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (isMutating) {
            return;
        }

        event.preventDefault();
    }, [isMutating]);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (isMutating) {
            return;
        }

        event.preventDefault();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, [isMutating]);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (isMutating || isFreemium) {
            return;
        }

        event.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        // Swallowed rather than ignored, so the browser doesn't open the file in the tab.
        if (isEditorUnavailable) {
            return;
        }
        const files = Array.from(event.dataTransfer.files);

        handleFileSelected(files);
    }, [handleFileSelected, isEditorUnavailable, isFreemium, isMutating]);

    return {
        isDropZoneActive,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    };
};
